import { useMemo, useState } from "react";
import { ErrorBanner } from "../../components/ui/ErrorBanner";
import { SectionHeader } from "../../components/ui/SectionHeader";
import { SkeletonTable } from "../../components/ui/Skeleton";
import { useQuotes } from "../../hooks/useQuotes";
import { MarketsTable } from "./MarketsTable";
import { compareBy, type SortKey } from "./sort";

/**
 * Markets section — live `/quotes` with default APY-desc sort, an
 * optional protocol filter, and a "top N" view-mode so the dashboard
 * can show the most profitable instruments without scrolling forever.
 */
export function MarketsSection({
	defaultLimit = 8,
}: {
	defaultLimit?: number;
}) {
	const { data, error, isLoading } = useQuotes();
	const [sortKey, setSortKey] = useState<SortKey>("apy");
	const [protocolFilter, setProtocolFilter] = useState<string>("all");
	const [showAll, setShowAll] = useState(false);

	const protocols = useMemo(() => {
		const set = new Set<string>(["all"]);
		for (const q of data ?? []) set.add(q.provider);
		return [...set];
	}, [data]);

	const rows = useMemo(() => {
		const list = (data ?? []).filter(
			(q) => protocolFilter === "all" || q.provider === protocolFilter,
		);
		return [...list].sort(compareBy(sortKey));
	}, [data, sortKey, protocolFilter]);

	const visible = showAll ? rows : rows.slice(0, defaultLimit);

	return (
		<section className="space-y-4">
			<SectionHeader
				title="Markets · most profitable first"
				subtitle={`${rows.length} quote${rows.length === 1 ? "" : "s"} · sorted by expected APY`}
				trailing={
					<div className="flex items-center gap-2 text-xs">
						<select
							className="rounded-md border border-slate-800 bg-slate-950 px-2 py-1"
							value={protocolFilter}
							onChange={(e) => setProtocolFilter(e.target.value)}
						>
							{protocols.map((p) => (
								<option key={p} value={p}>
									{p}
								</option>
							))}
						</select>
						<select
							className="rounded-md border border-slate-800 bg-slate-950 px-2 py-1"
							value={sortKey}
							onChange={(e) => setSortKey(e.target.value as SortKey)}
						>
							<option value="apy">APY ↓</option>
							<option value="exit">Exit ↑</option>
							<option value="asset">Asset ↑</option>
						</select>
					</div>
				}
			/>

			{isLoading && (
				<SkeletonTable
					rows={defaultLimit}
					cols={5}
					rowWidths={["w-6", "w-2/3", "w-1/2", "w-1/3", "w-1/3"]}
				/>
			)}
			{error && <ErrorBanner>{(error as Error).message}</ErrorBanner>}
			{data && <MarketsTable rows={visible} />}

			{rows.length > defaultLimit && (
				<button
					type="button"
					onClick={() => setShowAll((v) => !v)}
					className="text-xs text-emerald-400 hover:underline"
				>
					{showAll ? `Show top ${defaultLimit}` : `Show all ${rows.length}`}
				</button>
			)}
		</section>
	);
}
